/**
 * Thin fetch wrapper for the API server.
 *
 * The API is a separate origin. In dev, Vite proxies /api and /health to it
 * (see vite.config.ts), so requests stay same-origin in the browser. In
 * production, set VITE_API_BASE to the deployed API URL.
 */

const BASE = import.meta.env.VITE_API_BASE ?? ''

/**
 * Which session to authenticate with. Student and staff tokens are stored
 * separately, because one device may be used by both — a help-desk laptop, or
 * a manager's phone borrowed to re-open a pass.
 */
export type AuthKind = 'student' | 'staff' | 'none'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public detail?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Turns a rate-limit refusal into something a student can act on.
 *
 * The server answers 429 with `retryAfter` in seconds. "Something went wrong"
 * would be a lie — nothing went wrong, they are simply early — and a student
 * who is told nothing just keeps tapping, which is what tripped the limit.
 */
export function rateLimitMessage(err: unknown): string | null {
  const api = err as ApiError
  if (!(api instanceof ApiError) || api.status !== 429) return null

  const seconds = Number((api.detail as { retryAfter?: number } | undefined)?.retryAfter ?? 0)

  if (seconds >= 90) {
    const minutes = Math.ceil(seconds / 60)
    return `Too many attempts. Please try again in about ${minutes} minutes.`
  }
  if (seconds > 0) {
    return `Too many attempts. Please try again in ${seconds} seconds.`
  }
  return 'Too many attempts. Please wait a moment and try again.'
}

async function tokenFor(auth: AuthKind): Promise<string | null> {
  // Imported lazily so this module stays usable outside a browser.
  if (auth === 'staff') {
    const { getStaffSession } = await import('./staff')
    return getStaffSession()
  }
  if (auth === 'student') {
    const { getSession } = await import('./session')
    return getSession()
  }
  return null
}

export async function api<T>(
  path: string,
  init?: RequestInit & { auth?: AuthKind },
): Promise<T> {
  const { auth = 'student', ...rest } = init ?? {}
  const token = await tokenFor(auth)

  const res = await fetch(`${BASE}${path}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...rest.headers,
    },
  })

  const body = await res.json().catch(() => null)

  if (!res.ok) {
    throw new ApiError(res.status, body?.error ?? res.statusText, body)
  }
  return body as T
}

/**
 * Fetches a binary file — the certificate PDF — and hands the browser a blob
 * to save.
 *
 * The file is behind the student's session, and the token lives in
 * localStorage rather than a cookie, so a plain `<a href>` would arrive
 * unauthenticated. Fetching it here keeps the token in a header and out of
 * the URL, which is where it would otherwise end up in every access log.
 */
export async function download(
  path: string,
  filename: string,
  auth: AuthKind = 'student',
): Promise<void> {
  const token = await tokenFor(auth)

  const res = await fetch(`${BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })

  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, body?.error ?? res.statusText, body)
  }

  const url = URL.createObjectURL(await res.blob())
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()

  // Given to the download already; revoking immediately can cancel it on Safari.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export const get = <T>(path: string, auth: AuthKind = 'student') => api<T>(path, { auth })

export const post = <T>(path: string, data: unknown, auth: AuthKind = 'student') =>
  api<T>(path, { method: 'POST', body: JSON.stringify(data), auth })
